import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { token } from './renewalStyles';

const HeroSection = styled.section`
  position: relative;
  height: 260vh;
  background: ${token.heroBg};
  color: #fff;
`;

const Stage = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 120px 32px 80px;
`;

const Glow = styled.div`
  position: absolute;
  inset: 0;
  background: ${token.heroGlow};
`;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.9;
  pointer-events: none;
`;

const TerminalLog = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  opacity: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  line-height: 1.6;
  text-align: left;
  white-space: pre;

  @media (max-width: 1279px) {
    display: none;
  }
`;

const LogLine = styled.div`
  position: absolute;
`;

const StateA = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  will-change: opacity, transform;
`;

const HeroEyebrow = styled.div`
  font-size: 13px;
  letter-spacing: 0.4em;
  color: ${token.primary};
  font-weight: 700;
  margin-bottom: 40px;

  @media screen and (max-width: 768px) {
    font-size: 11px;
    letter-spacing: 0.28em;
    margin-bottom: 28px;
  }
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(52px, 9vw, 132px);
  font-weight: 800;
  line-height: 0.98;
  letter-spacing: -0.04em;
  color: #fff;

  span.brand {
    color: ${token.primary};
  }
`;

const HeroWeave = styled.p`
  margin: 56px auto 0;
  font-size: clamp(20px, 2.4vw, 32px);
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #fff;
`;

const StateB = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 120px 32px;
  opacity: 0;
  pointer-events: none;
  will-change: opacity, transform;
`;

const StateBTitle = styled.h2`
  margin: 0;
  max-width: 1100px;
  font-size: clamp(34px, 5.4vw, 78px);
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.035em;
  color: #fff;
`;

const StateBCopy = styled.p`
  margin: 36px auto 0;
  max-width: 720px;
  font-size: clamp(14px, 1.4vw, 18px);
  line-height: 1.85;
  color: ${token.heroMuted};
`;

const StateBCta = styled.a`
  margin-top: 44px;
  display: inline-block;
  background: ${token.primary};
  color: #fff;
  padding: 15px 34px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.18em;
  transition: transform 0.25s ${token.ease}, background 0.25s;

  &:hover {
    color: #fff;
    background: ${token.primaryHover};
    text-decoration: none;
  }
`;

const ScrollDown = styled.a`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 44px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: ${token.faint};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.2em;

  &:hover {
    color: ${token.faint};
    text-decoration: none;
  }

  span {
    font-size: 16px;
    animation: brit-bounce 1.6s ease-in-out infinite;
  }
`;

const LOGS = [
  {
    style: { left: '6%', top: '12%', color: token.amber, opacity: 0.75 },
    text: '==119314== brit-scan, dependency audit\n==119314== 0 critical, 2 moderate',
  },
  {
    style: { right: '7%', top: '18%', color: token.heroMuted, opacity: 0.6 },
    text: 'push    %rbp\nmov     %rsp,%rbp\ncall    0x00000000004011a6',
  },
  {
    style: { right: '9%', bottom: '22%', color: token.heroMuted, opacity: 0.6 },
    text: 'stp     x29, x30, [sp, #-16]!\nmov     x29, sp\nldr     x0, [x19, #24]',
  },
  {
    style: { left: '8%', bottom: '16%', color: token.amber, opacity: 0.7 },
    text: '$ kubectl rollout status deploy/brit-api\ndeployment "brit-api" successfully rolled out',
  },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * 스크롤 진행도(p) 하나로 상태 A/B · 터미널 로그 · 지구본 줌을 모두 구동합니다.
 * p = clamp(scrollY / (heroHeight - vh), 0, 1), 보간은 smoothstep.
 */
export default function Hero() {
  const rootRef = useRef(null);
  const canvasRef = useRef(null);
  const stateARef = useRef(null);
  const stateBRef = useRef(null);
  const logRef = useRef(null);
  const scrollHintRef = useRef(null);
  const ctaRef = useRef(null);

  const heroP = useRef(0);
  const scrollV = useRef(0);
  const pointer = useRef({ x: null, y: null });

  /* ---------------------------------------------------------- 스크롤 진행도 */
  useEffect(() => {
    let raf = null;
    let lastY = window.scrollY;

    const update = () => {
      raf = null;
      const hero = rootRef.current;
      if (!hero) return;

      scrollV.current = window.scrollY - lastY;
      lastY = window.scrollY;

      const span = hero.offsetHeight - window.innerHeight;
      const p = span > 0 ? Math.min(1, Math.max(0, window.scrollY / span)) : 0;
      heroP.current = p;

      const sm = (a, b) => {
        const t = Math.min(1, Math.max(0, (p - a) / (b - a)));
        return t * t * (3 - 2 * t);
      };

      const a = stateARef.current;
      if (a) {
        const out = sm(0.04, 0.3);
        a.style.opacity = String(1 - out);
        a.style.transform = `translateY(${-p * 60}px) scale(${1 - out * 0.08})`;
      }

      const b = stateBRef.current;
      if (b) {
        const o = sm(0.42, 0.68);
        b.style.opacity = String(o);
        b.style.pointerEvents = o > 0.5 ? 'auto' : 'none';
        b.style.transform = `translateY(${26 - o * 26}px)`;
      }

      const log = logRef.current;
      if (log) log.style.opacity = String(sm(0.18, 0.5) * (1 - sm(0.9, 1)));

      const hint = scrollHintRef.current;
      if (hint) hint.style.opacity = String(1 - sm(0, 0.12));
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  /* -------------------------------------------------------- 3D 파티클 지구본 */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduce = prefersReducedMotion();

    // Fibonacci sphere 로 균등 분포된 점 2600개
    const N = 2600;
    const pts = [];
    const gold = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < N; i += 1) {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const th = gold * i;
      pts.push({ x: Math.cos(th) * r, y, z: Math.sin(th) * r, hot: i % 37 === 0 });
    }

    // 와이어프레임 케이지 — 위도 링 7줄 + 경도 링 9줄
    const rings = [];
    for (let k = 0; k < 7; k += 1) {
      const lat = -0.78 + k * 0.26;
      const ring = [];
      const rr = Math.sqrt(Math.max(0, 1 - lat * lat));
      for (let a = 0; a <= 84; a += 1) {
        const t = (a / 84) * Math.PI * 2;
        ring.push({ x: Math.cos(t) * rr, y: lat, z: Math.sin(t) * rr });
      }
      rings.push(ring);
    }
    for (let m = 0; m < 9; m += 1) {
      const lon = (m / 9) * Math.PI;
      const ring = [];
      for (let a = 0; a <= 84; a += 1) {
        const t = (a / 84) * Math.PI * 2;
        ring.push({ x: Math.sin(t) * Math.cos(lon), y: Math.cos(t), z: Math.sin(t) * Math.sin(lon) });
      }
      rings.push(ring);
    }

    let W = 0;
    let H = 0;
    let R = 0;
    let rot = 0;
    let tiltX = 0;
    let tiltY = 0;
    let tx = 0;
    let ty = 0;
    let raf = null;
    const t0 = performance.now();
    const ink = '198,214,255';

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      // setTransform(누적 대신 절대 지정) — 리사이즈마다 scale 이 곱해지는 것을 방지
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      R = Math.min(W, H) * 0.34;
    };

    const frame = () => {
      ctx.clearRect(0, 0, W, H);
      scrollV.current *= 0.92;
      if (!reduce) rot += 0.0022 + Math.min(0.02, Math.abs(scrollV.current) * 0.00012);
      tiltX += (tx - tiltX) * 0.06;
      tiltY += (ty - tiltY) * 0.06;

      const cx = W / 2;
      const cy = H / 2;
      const sinR = Math.sin(rot);
      const cosR = Math.cos(rot);
      const sinT = Math.sin(tiltY);
      const cosT = Math.cos(tiltY);

      const el = Math.min(1, (performance.now() - t0) / 1600);
      const ease = 1 - Math.pow(1 - el, 3);
      const hp = heroP.current || 0;
      const zEase = hp * hp * (3 - 2 * hp);
      const zoom = 4.6 - zEase * 3.6;
      const grow = (reduce ? 1 : 0.55 + ease * 0.45) * zoom;

      const now = performance.now() * 0.001;
      const hotA = now * 0.55;
      const hn = { x: Math.cos(hotA), y: 0.42, z: Math.sin(hotA) };
      const hl = Math.hypot(hn.x, hn.y, hn.z);
      hn.x /= hl;
      hn.y /= hl;
      hn.z /= hl;

      const project = p => {
        const x = p.x * cosR - p.z * sinR;
        let z = p.x * sinR + p.z * cosR;
        const y = p.y * cosT - z * sinT;
        z = p.y * sinT + z * cosT;
        const persp = 1 / (2.4 - z);
        return {
          sx: cx + x * R * persp * 2.4 * grow + tiltX * 26,
          sy: cy + y * R * persp * 2.4 * grow,
          z,
        };
      };

      ctx.lineWidth = 1;
      ctx.strokeStyle = `rgba(${ink},1)`;
      rings.forEach(ring => {
        ctx.beginPath();
        let started = false;
        ring.forEach(p => {
          const q = project(p);
          // 뒷면(z < -0.1)은 선을 끊어 감춥니다.
          if (q.z < -0.1) {
            started = false;
            return;
          }
          if (!started) {
            ctx.moveTo(q.sx, q.sy);
            started = true;
          } else {
            ctx.lineTo(q.sx, q.sy);
          }
        });
        ctx.globalAlpha = (0.1 + (1 - zEase) * 0.26) * ease;
        ctx.stroke();
      });

      pts.forEach(p => {
        const x = p.x * cosR - p.z * sinR;
        let z = p.x * sinR + p.z * cosR;
        const y = p.y * cosT - z * sinT;
        z = p.y * sinT + z * cosT;

        const persp = 1 / (2.4 - z);
        const sx = cx + x * R * persp * 2.4 * grow + tiltX * 26;
        const sy = cy + y * R * persp * 2.4 * grow;
        const depth = (z + 1) / 2;
        // 시간에 따라 회전하는 법선 근처의 점을 밝게 — 트래블링 핫 아크
        const band = 1 - Math.min(1, Math.abs(p.x * hn.x + p.y * hn.y + p.z * hn.z) / 0.055);

        let near = 0;
        if (pointer.current.x != null) {
          const dd = Math.hypot(sx - pointer.current.x, sy - pointer.current.y);
          if (dd < 150) near = Math.pow(1 - dd / 150, 2);
        }

        const size = (0.5 + depth * 1.7) * (1 + band * 1.6 + near * 1.4);
        ctx.globalAlpha = Math.min(1, (0.08 + depth * 0.62 + band * 0.7 + near * 0.5) * ease);
        if (band > 0.25) {
          ctx.fillStyle = `rgba(255,${Math.round(255 - band * 130)},${Math.round(255 - band * 150)},1)`;
        } else if (p.hot) {
          ctx.fillStyle = `rgba(230,38,41,${0.3 + depth * 0.7})`;
        } else {
          ctx.fillStyle = `rgba(${ink},1)`;
        }
        if (band > 0.5) {
          ctx.shadowBlur = 10 * band;
          ctx.shadowColor = 'rgba(255,190,190,0.9)';
        }
        ctx.beginPath();
        ctx.arc(sx, sy, size, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    };

    const onMove = e => {
      tx = (e.clientX / window.innerWidth - 0.5) * 2;
      ty = (e.clientY / window.innerHeight - 0.5) * 0.5;
      const rect = canvas.getBoundingClientRect();
      pointer.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMove, { passive: true });
    resize();
    frame();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  /* ------------------------------------------------------------- 마그네틱 CTA */
  useEffect(() => {
    const btn = ctaRef.current;
    if (!btn || prefersReducedMotion()) return undefined;

    const onMove = e => {
      const r = btn.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      if (Math.hypot(dx, dy) < 160) {
        btn.style.transform = `translate(${dx * 0.22}px, ${dy * 0.22}px) scale(1.05)`;
      } else {
        btn.style.transform = 'none';
      }
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <HeroSection id="hero" ref={rootRef}>
      <Stage>
        <Glow />
        <Canvas ref={canvasRef} aria-hidden="true" />

        <TerminalLog ref={logRef} aria-hidden="true">
          {LOGS.map(log => (
            <LogLine key={log.text} style={log.style}>
              {log.text}
            </LogLine>
          ))}
        </TerminalLog>

        <StateA ref={stateARef}>
          <HeroEyebrow>BENEFIT RENEW INFORMATION TECHNOLOGY</HeroEyebrow>
          <HeroTitle>
            <span className="brand">BRIT.</span>
            <br />
            Development
            <br />
            Company
          </HeroTitle>
          <HeroWeave>WEAVE BEYOND</HeroWeave>
        </StateA>

        <StateB ref={stateBRef}>
          <StateBTitle>
            코드 너머 더 나은 삶을
            <br />
            엮어내는 BRIT
          </StateBTitle>
          <StateBCopy>
            SI/SM, 모바일 웹·앱 구축, 솔루션 공급, IT 컨설팅.
            <br />
            기술의 경계를 넘어 디지털 세상의 새로운 가능성을 현실로 만들어갑니다.
          </StateBCopy>
          <StateBCta href="#overview" ref={ctaRef}>
            VIEW MORE ▸
          </StateBCta>
        </StateB>

        <ScrollDown href="#overview" ref={scrollHintRef}>
          SCROLL DOWN <span>↓</span>
        </ScrollDown>
      </Stage>
    </HeroSection>
  );
}
